/* HC - IB Gift Choice UI (workstream A5) - 2026-07-21
 *
 * The shopper-facing renderer for the first-party gift engine's CHOICE rewards.
 * Pending choices open a true OVERLAY MODAL above everything, including the cart
 * drawer. Rebuilt 2026-07-21 to Noah's approved mockup (a "Choose your free
 * gift" picker): tiles SELECT, the CTA commits. One rule at a time — multiple
 * pending rules QUEUE (a small "OFFER 1 OF 2" marker orients the shopper), and
 * committing a rule plays a brief "Added to your order" beat before advancing.
 *
 * Pure engine consumer:
 *   Listens : document 'ibgift:state'          (desired state incl. choice slots)
 *             document 'ibgift:choices-updated' (optimistic nudge)
 *             document 'cart:updated'           (re-badge reward line items + toast)
 *   Calls   : window.IBGiftEngine.selectChoice(ruleId, variantId)  (append-only —
 *             one call per NEW pick; committed picks are never re-sent)
 *
 * Hard invariants:
 *   - NEVER touch the cart directly (no cart layer, no /cart AJAX). The engine's
 *     reconciler owns cart mutations; this UI only records the choice on commit.
 *   - NEVER make a network request. Option display data (title/image/size/value)
 *     must arrive in the manifest; absent it, render gracefully with what exists.
 *   - Fail silent to nothing: if the engine or a mount is absent, render nothing
 *     and throw nothing. Everything is behind settings.gift_engine_enabled.
 *
 * The render core (buildModel / buildModalView / renderModalView / toggleSelection
 * / commitSelection) is pure and exposed on window.IBGiftChoiceUI._test so it can
 * be unit-tested without a DOM.
 */
(function () {
  'use strict';


  /* Five rotating pastel radial gradients — the tile backdrop when an option
   * carries no product image. Verbatim from the approved mockup. */
  var GRADIENTS = [
    'radial-gradient(120% 90% at 50% 14%, rgba(255,255,255,0.7), #E3ECF2 68%, #D6E2EC 100%)',
    'radial-gradient(120% 90% at 50% 14%, rgba(255,255,255,0.7), #F5E2E4 68%, #EFD3D7 100%)',
    'radial-gradient(120% 90% at 50% 14%, rgba(255,255,255,0.7), #EAE4F0 68%, #DED4EC 100%)',
    'radial-gradient(120% 90% at 50% 14%, rgba(255,255,255,0.7), #E4EBDD 68%, #D6E1CB 100%)',
    'radial-gradient(120% 90% at 50% 14%, rgba(255,255,255,0.7), #F1EADE 68%, #E7DCCB 100%)'
  ];

  /* Inline hairline glyphs (currentColor) — no icon library. */
  var SVG = {
    /* the gift knot, used in the header medallion + drawer re-entry row */
    gift:
      '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" ' +
      'stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
      '<rect x="3" y="8" width="18" height="4" rx="1"></rect><path d="M12 8v13"></path>' +
      '<path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7"></path>' +
      '<path d="M7.5 8a2.5 2.5 0 0 1 0-5C10 3 12 8 12 8s2-5 4.5-5a2.5 2.5 0 0 1 0 5"></path></svg>',
    /* the checkmark, used on selection dots + line-item badge */
    check:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" ' +
      'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
      '<path d="M20 6 9 17l-5-5"></path></svg>',
    /* the close X */
    close:
      '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" ' +
      'stroke-width="2" stroke-linecap="round" aria-hidden="true" focusable="false">' +
      '<path d="M18 6 6 18M6 6l12 12"></path></svg>'
  };

  /* The bottle silhouette placeholder that sits over the pastel gradient when an
   * option has no image (verbatim from the mockup). */
  var BOTTLE_SVG =
    '<svg viewBox="0 0 60 88" width="46" height="67" fill="none" aria-hidden="true" ' +
    'focusable="false" style="display:block;position:relative;z-index:1;">' +
    '<ellipse cx="30" cy="83" rx="18" ry="3.4" fill="#1C1614" opacity="0.06"></ellipse>' +
    '<rect x="24" y="5" width="12" height="9" rx="2" fill="#EFE6D7" stroke="#E1D6C4" stroke-width="1"></rect>' +
    '<path d="M23 14 h14 v3 c5 1.5 7 5 7 11 v45 c0 6 -4 10 -10 10 h-8 c-6 0 -10 -4 -10 -10 v-45 c0 -6 2 -9.5 7 -11 z" ' +
    'fill="#FFFFFF" stroke="#E4DACB" stroke-width="1.3"></path>' +
    '<circle cx="22" cy="45" r="2" fill="none" stroke="#C88" stroke-width="1.1"></circle>' +
    '<circle cx="27.6" cy="45" r="2" fill="none" stroke="#C88" stroke-width="1.1"></circle>' +
    '<circle cx="22" cy="50.4" r="2" fill="none" stroke="#C88" stroke-width="1.1"></circle>' +
    '<circle cx="27.6" cy="50.4" r="2" fill="none" stroke="#C88" stroke-width="1.1"></circle>' +
    '<rect x="19" y="57" width="22" height="1.8" rx="0.9" fill="#D9D0C2"></rect>' +
    '<rect x="19" y="61" width="15" height="1.6" rx="0.8" fill="#E3DBCE"></rect></svg>';

  /* ------------------------------------------------------------------ *
   * Small utilities
   * ------------------------------------------------------------------ */

  function hasDoc() {
    return typeof document !== 'undefined' && document && document.addEventListener;
  }

  function engineApi() {
    return (typeof window !== 'undefined' && window.IBGiftEngine) || null;
  }

  /* Theme setting bridge: the loader snippet stamps this global from
   * settings.gift_show_value. Absent (preview/tests) -> value display defaults
   * on, but only ever surfaces when an option actually carries price data. */
  function readConfig() {
    var cfg = (typeof window !== 'undefined' && window.__IB_GIFT_CHOICE_CONFIG__) || {};
    return { showValue: cfg.showValue !== false };
  }

  function esc(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /* href/link URLs from manifest data: only same-origin relative or http(s).
   * Never javascript:/data:/vbscript: in a navigable link. */
  function safeUrl(value) {
    if (typeof value !== 'string' || !value) return '';
    var trimmed = value.replace(/^\s+/, '');
    if (/^https?:\/\//i.test(trimmed) || trimmed.charAt(0) === '/') return trimmed;
    return '';
  }

  /* Image src is more permissive than a link: a data:image/* URI renders a
   * picture and cannot execute script, so allow it (a manifest could inline a
   * tiny thumbnail). Still reject data:text/html and other actives. */
  function safeImageUrl(value) {
    if (typeof value !== 'string' || !value) return '';
    var trimmed = value.replace(/^\s+/, '');
    if (/^data:image\//i.test(trimmed)) return trimmed;
    return safeUrl(trimmed);
  }

  function num(value, fallback) {
    var n = Number(value);
    return isFinite(n) ? n : fallback;
  }

  /* A positive finite retail value, or null. The manifest omits value today;
   * it lights up the strikethrough + "VALUE" tag once options carry it. */
  function normValue(raw) {
    if (raw == null || raw === '') return null;
    var n = Number(raw);
    if (!isFinite(n) || n <= 0) return null;
    return n;
  }

  function money(n) { return '$' + Number(n).toFixed(2); }

  /* ------------------------------------------------------------------ *
   * Model — pure. Turns an ibgift:state detail into render-ready cards.
   * `prevRuleIds` is the set of choice ruleIds shown last render, so a rule
   * that vanished (threshold lost) can be surfaced once, quietly.
   * ------------------------------------------------------------------ */

  function normOption(raw) {
    raw = raw || {};
    var variantId = raw.variantId != null ? raw.variantId : raw.v;
    var url = safeUrl(raw.url);
    if (!url && typeof raw.handle === 'string' && raw.handle) {
      url = '/products/' + encodeURIComponent(raw.handle);
    }
    return {
      variantId: variantId != null ? String(variantId) : '',
      qty: num(raw.quantity != null ? raw.quantity : raw.qty, 1) || 1,
      title: (typeof raw.title === 'string' && raw.title) ? raw.title : '',
      brand: (typeof raw.brand === 'string' && raw.brand) ? raw.brand : '',
      /* size ("950 ml") and value are v-next display fields; absent today, the
       * card degrades cleanly (no size row, no value tag). */
      size: (typeof raw.size === 'string' && raw.size) ? raw.size : '',
      value: normValue(raw.value != null ? raw.value : raw.price),
      image: safeImageUrl(raw.image || raw.featured_image || raw.imageUrl),
      url: url,
      oos: raw.oos === true || raw.available === false
    };
  }

  function buildCard(choice) {
    var slots = num(choice.slots, 0) || 0;
    var options = [];
    var srcOptions = choice.options || [];
    for (var i = 0; i < srcOptions.length; i++) options.push(normOption(srcOptions[i]));

    var selectedVariantIds = [];
    var srcSelected = choice.selected || [];
    for (var s = 0; s < srcSelected.length; s++) {
      var norm = normOption(srcSelected[s]);
      if (norm.variantId) selectedVariantIds.push(norm.variantId);
    }

    var optionByVariant = {};
    for (var o = 0; o < options.length; o++) optionByVariant[options[o].variantId] = options[o];

    var chosenOos = [];
    var chosenNames = [];
    for (var c = 0; c < selectedVariantIds.length; c++) {
      var opt = optionByVariant[selectedVariantIds[c]];
      if (opt && opt.oos) chosenOos.push(selectedVariantIds[c]);
      if (opt && opt.title) chosenNames.push(opt.title);
    }

    var filledSlots = selectedVariantIds.length;
    var needsChoice = filledSlots < slots;

    return {
      kind: 'choice',
      ruleId: String(choice.ruleId),
      version: choice.version,
      slots: slots,
      options: options,
      selectedVariantIds: selectedVariantIds,
      needsChoice: needsChoice,
      sealed: !needsChoice && slots > 0,
      chosenOos: chosenOos,
      chosenNames: chosenNames
    };
  }

  function buildModel(detail, prevRuleIds) {
    detail = detail || {};
    prevRuleIds = prevRuleIds || {};
    var cards = [];
    var activeRuleIds = {};
    var choices = detail.choices || [];

    /* HC - modal is pending-choice-only (Noah, 2026-07-20) - 2026-07-20
     * A card exists ONLY while a rule still needs a pick. Completed choices and
     * auto-added single-option gifts get NO card: the line badge plus the theme
     * toast are the whole confirmation. Rules that are live-but-unrendered still
     * count as active so they never emit a phantom "offer removed" card. */
    for (var i = 0; i < choices.length; i++) {
      var choice = choices[i];
      if (!choice || choice.ruleId == null) continue;
      var card = buildCard(choice);
      if (card.slots <= 0) continue;
      activeRuleIds[card.ruleId] = true;
      /* A completed pick whose chosen option went OOS is NOT resolved — the
       * engine cannot insert it, so the picker must resurface for a re-pick. */
      if (!card.needsChoice && !card.chosenOos.length) continue;
      cards.push(card);
    }

    var reasons = detail.reasons || [];
    for (var ar = 0; ar < reasons.length; ar++) {
      if (reasons[ar] && reasons[ar].code === 'auto_single_option') {
        activeRuleIds[String(reasons[ar].ruleId)] = true;
      }
    }

    /* Threshold lost: a rule shown last render that is no longer live at all. */
    for (var ruleId in prevRuleIds) {
      if (!Object.prototype.hasOwnProperty.call(prevRuleIds, ruleId)) continue;
      if (activeRuleIds[ruleId]) continue;
      cards.push({ kind: 'lost', ruleId: ruleId });
    }

    return { cards: cards, activeRuleIds: activeRuleIds };
  }

  function choiceCardsOf(cards) {
    var out = [];
    for (var i = 0; i < (cards || []).length; i++) {
      if (cards[i].kind === 'choice') out.push(cards[i]);
    }
    return out;
  }

  /* ------------------------------------------------------------------ *
   * Selection semantics — pure. The selection is a MULTISET (an array that may
   * hold the same variant more than once): the engine now stores one pick per
   * earned slot with duplicates legal (review F12 — a 36-tube cart earns two
   * Smoothies slots and the shopper may want the same emulsion twice).
   *
   *   Single-pick (maxPick <= 1): pure radio — re-clicking the chosen tile
   *     clears it, clicking another replaces it.
   *   Multi-pick: below the cap, a click adds one unit of that tile (so a second
   *     click on a chosen tile adds a second unit -> a "x2" chip). AT the cap, a
   *     click on a chosen tile steps it down one unit; a click on an un-chosen
   *     tile swaps out the oldest unit for it.
   * `maxPick` is the REMAINING slot count, never total — the single authority
   * for the cap (review F10).
   * ------------------------------------------------------------------ */

  function toggleSelection(sel, id, maxPick) {
    sel = (sel || []).map(String);
    id = String(id);

    if (maxPick <= 1) {
      /* single: re-click clears, otherwise replace */
      return (sel.length === 1 && sel[0] === id) ? [] : [id];
    }
    if (sel.length < maxPick) return sel.concat([id]); /* room: add a unit (dups ok) */

    /* at cap */
    var last = sel.lastIndexOf(id);
    if (last !== -1) return sel.slice(0, last).concat(sel.slice(last + 1)); /* step down a unit */
    return sel.slice(1).concat([id]);              /* swap the oldest unit out */
  }

  /* ------------------------------------------------------------------ *
   * Commit state machine — pure (review F2). A modal commit is not confirmed
   * until the engine's reconciler reports its cart mutation resolved via the
   * `ibgift:mutation-result` event; recording the choice is not the same as the
   * gift landing in the cart. Phases: idle -> adding -> (added | error). The
   * reducer is driven by fake events in tests.
   *
   *   state = { phase, ruleId, variantIds }
   *   events:
   *     { type:'commit',  ruleId, variantIds }
   *     { type:'result',  ok, addedVariantIds, failedRules:[{ruleId,variantId,reason}] }
   *     { type:'timeout' }   (no result within the deadline)
   *     { type:'retry' }     (shopper retries after an error)
   *     { type:'reset' }     (beat finished / modal closed)
   * ------------------------------------------------------------------ */

  function commitTransition(state, event) {
    state = state || { phase: 'idle', ruleId: null, variantIds: [] };
    var e = event || {};
    if (e.type === 'commit') {
      return { phase: 'adding', ruleId: String(e.ruleId), variantIds: (e.variantIds || []).map(String) };
    }
    if (e.type === 'retry') {
      if (state.phase !== 'error') return state;
      return { phase: 'adding', ruleId: state.ruleId, variantIds: state.variantIds };
    }
    if (e.type === 'result') {
      /* 'error' also accepts a matching ok — a transient failure the engine
       * self-retried lands a later result carrying OUR variant (team-lead
       * 2026-07-21). 'adding' fails on failedRules for our rule; 'error' does
       * not re-fail (it's already there and watching for recovery).
       *
       * A commit is 'added' ONLY when EVERY committed variant is acknowledged
       * added — a partial (some ok, some failed) must NOT beat as full success
       * (review R2-3). A partial that carries a failure for our rule drops to
       * 'error' so the unfulfilled remainder re-presents. */
      if (state.phase !== 'adding' && state.phase !== 'error') return state;
      var need = state.variantIds.length;
      var failed = false;
      var fr = e.failedRules || [];
      for (var i = 0; i < fr.length; i++) {
        if (fr[i] && String(fr[i].ruleId) === state.ruleId) failed = true;
      }
      var set = {};
      var av = e.addedVariantIds || [];
      for (var a = 0; a < av.length; a++) set[String(av[a])] = true;
      var allAdded = need > 0;
      for (var v = 0; v < state.variantIds.length; v++) {
        if (!set[state.variantIds[v]]) allAdded = false;
      }
      if (allAdded) return { phase: 'added', ruleId: state.ruleId, variantIds: state.variantIds };
      if (failed && state.phase === 'adding') return { phase: 'error', ruleId: state.ruleId, variantIds: state.variantIds };
      return state; /* partial-so-far, or a result for some other commit — keep waiting */
    }
    if (e.type === 'timeout') {
      if (state.phase !== 'adding') return state;
      return { phase: 'error', ruleId: state.ruleId, variantIds: state.variantIds };
    }
    if (e.type === 'reset') return { phase: 'idle', ruleId: null, variantIds: [] };
    return state;
  }

  /* ------------------------------------------------------------------ *
   * Modal view-model — pure. Everything the markup needs, precomputed, so the
   * renderer is a straight string build and the live controller can diff/update
   * without re-deriving. Returns null when there is nothing to show.
   *
   * input = {
   *   card,             the choice card currently on stage
   *   selectedIds,      working selection multiset (array of variantId strings,
   *                     duplicates allowed for multi-pick)
   *   markerPosition,   this rule's 1-based position in the session queue
   *   markerTotal,      number of rules in the session queue (marker shows if >1)
   *   commitPhase,      'idle' | 'adding' | 'added' | 'error' (async commit, F2)
   *   added,            legacy alias for commitPhase === 'added'
   *   isMobile, viewportW, showValue
   * }
   * ------------------------------------------------------------------ */

  function buildModalView(input) {
    input = input || {};
    var card = input.card;
    if (!card || card.kind !== 'choice') return null;

    /* DELTA presentation (Noah 2026-07-21): the modal counts only what is still
     * pickable. Committed picks already live in the cart (as FREE GIFT lines), so
     * they are subtracted from the earned slot count — the shopper never re-picks
     * what they already have. An OOS committed pick never counts as fulfilled, so
     * it reopens its slot for a re-pick rather than blocking one. */
    var totalSlots = num(card.slots, 1) || 1;
    var committedOos = (card.chosenOos && card.chosenOos.length) || 0;
    var committedPicks = Math.max(0,
      (card.selectedVariantIds ? card.selectedVariantIds.length : 0) - committedOos);
    var reopened = committedPicks > 0;
    var maxPick = Math.max(1, totalSlots - committedPicks);
    var isSingle = maxPick <= 1;
    var showValue = input.showValue !== false;
    var isMobile = !!input.isMobile;
    var vw = num(input.viewportW, 1200);

    /* Keep only real, in-stock selection UNITS (duplicates preserved for multi-
     * pick; an OOS tile can never be picked). Then cap at the REMAINING slots —
     * this sanitized list is the single authority the commit sends (review F10).*/
    var optByVariant = {};
    for (var o = 0; o < card.options.length; o++) optByVariant[card.options[o].variantId] = card.options[o];
    var selectedIds = [];
    var src = input.selectedIds || [];
    for (var s = 0; s < src.length; s++) {
      var vId = String(src[s]);
      var so = optByVariant[vId];
      if (so && !so.oos) selectedIds.push(vId);
    }
    if (isSingle && selectedIds.length > 1) selectedIds = selectedIds.slice(-1);
    if (selectedIds.length > maxPick) selectedIds = selectedIds.slice(-maxPick);

    var count = selectedIds.length;              /* total UNITS selected this session */
    var atCap = !isSingle && count >= maxPick;

    /* Per-variant unit counts (multiset), for the "x2" chip. */
    var unitCount = {};
    for (var u = 0; u < selectedIds.length; u++) {
      unitCount[selectedIds[u]] = (unitCount[selectedIds[u]] || 0) + 1;
    }

    /* Commit phase (review F2). 'idle' | 'adding' | 'added' | 'error'. The legacy
     * `added` boolean still maps to the 'added' beat for older callers/tests. */
    var phase = input.commitPhase || (input.added ? 'added' : 'idle');
    var isAdded = phase === 'added';
    var isAdding = phase === 'adding';
    var isError = phase === 'error';
    var busy = isAdding || isAdded; /* selection is frozen while a commit resolves */

    /* Cards. Each precomputes its own selected/count/dim/oos flags + value tag. */
    var cards = [];
    var valueTotal = 0;
    var allSelectedPriced = count > 0;
    for (var i = 0; i < card.options.length; i++) {
      var opt = card.options[i];
      var c = unitCount[opt.variantId] || 0;
      var selected = c > 0;
      /* At cap, an un-chosen tile is de-emphasized — clicking it swaps (still
       * interactive, review F12), not blocked. */
      var dim = !selected && !opt.oos && atCap;
      var hasValue = showValue && opt.value != null;
      if (selected) {
        if (opt.value != null) valueTotal += opt.value * c;
        else allSelectedPriced = false;
      }
      cards.push({
        variantId: opt.variantId,
        brand: opt.brand,
        name: opt.title || 'Free gift',
        size: opt.size,
        image: opt.image,
        gradient: GRADIENTS[i % GRADIENTS.length],
        selected: selected,
        count: c,
        oos: opt.oos,
        dim: dim,
        hasValue: hasValue,
        valueStrike: hasValue ? money(opt.value) : '',
        valueTag: hasValue ? ('$' + Math.round(opt.value) + ' value') : ''
      });
    }

    var hasValueTotal = showValue && count > 0 && allSelectedPriced;

    /* Footer summary + CTA vary by phase. */
    var summaryTop, summarySub, summaryTone = 'ink';
    var ctaLabel = 'Add to my order';
    var ctaEnabled = false;
    if (isAdded) {
      summaryTop = 'Added to your order ✓';
      summarySub = count + ' free gift' + (count === 1 ? '' : 's') + ' added';
      summaryTone = 'ok';
    } else if (isAdding) {
      summaryTop = 'Adding your gift' + (count === 1 ? '' : 's') + '…';
      summarySub = 'One moment';
      ctaLabel = 'Adding…';
    } else if (isError) {
      summaryTop = 'Couldn’t add — try again';
      summarySub = 'Your gift wasn’t added';
      summaryTone = 'error';
      ctaLabel = 'Try again';
      ctaEnabled = true;
    } else if (count === 0) {
      summaryTop = 'Select a gift to continue';
      summarySub = isSingle ? 'Choose one · all free' : 'Pick up to ' + maxPick + ' · all free';
    } else {
      summaryTop = count + ' free gift' + (count === 1 ? '' : 's') + ' selected';
      summarySub = hasValueTotal ? (money(valueTotal) + ' total value') : 'All complimentary';
      ctaEnabled = true;
    }

    /* Queue marker: the current rule's 1-based position among the session's rules
     * (review F11 — a PARTIAL commit keeps the same rule, so committed-pick math
     * can't drive this; the controller supplies the rule position directly). */
    var position = Math.max(1, num(input.markerPosition, 1));
    var total = Math.max(position, num(input.markerTotal, 1));

    /* Grid: 4/3/2 columns by width — 2-col floor on phones (Noah 2026-07-21:
       full-width mobile cards were too tall, too much scrolling). */
    var cols = vw >= 1080 ? 4 : vw >= 820 ? 3 : 2;
    var effCols = Math.min(cols, Math.max(cards.length, 1));
    var few = effCols < cols;

    /* Availability line. */
    var availStatus, availTone;
    if (atCap) { availStatus = 'Max reached — tap a gift to swap'; availTone = 'rouge'; }
    else { availStatus = 'All complimentary'; availTone = 'caramel'; }

    return {
      ruleId: card.ruleId,
      maxPick: maxPick,
      remaining: maxPick,
      committedPicks: committedPicks,
      reopened: reopened,
      isSingle: isSingle,
      isMobile: isMobile,
      phase: phase,
      added: isAdded,
      adding: isAdding,
      errored: isError,
      busy: busy,
      cards: cards,
      selectedIds: selectedIds,
      count: count,
      atCap: atCap,
      pct: maxPick ? Math.min(100, (count / maxPick) * 100) : 0,
      giftNoun: isSingle ? 'gift' : 'gifts',
      favNoun: isSingle ? 'favorite' : 'favorites',
      singlePrompt: count ? 'Your free gift is selected' : 'Choose one free gift below',
      total: card.options.length,
      availStatus: availStatus,
      availTone: availTone,
      hasValueTotal: hasValueTotal,
      valueTotal: valueTotal,
      summaryTop: summaryTop,
      summarySub: summarySub,
      summaryTone: summaryTone,
      ctaLabel: ctaLabel,
      ctaEnabled: ctaEnabled,
      marker: { show: total > 1, position: position, total: total, label: 'Offer ' + position + ' of ' + total },
      grid: {
        cols: effCols,
        few: few,
        template: 'repeat(' + effCols + ',' + (few ? 'minmax(0,280px)' : '1fr') + ')',
        justify: few ? 'center' : 'stretch'
      }
    };
  }

  /* ------------------------------------------------------------------ *
   * View — pure. view-model -> HTML string. No DOM required.
   * ------------------------------------------------------------------ */

  function cardHtml(c, isSingle) {
    var media = c.image
      ? '<img class="ibg-card-img" src="' + esc(c.image) + '" alt="' + esc(c.name) +
        '" loading="lazy" onerror="this.style.display=\'none\'">'
      : BOTTLE_SVG;

    var badge = c.oos
      ? '<span class="ibg-badge ibg-badge--oos">Sold out</span>'
      : '<span class="ibg-badge">Free</span>';

    /* Selection dot: single -> inner dot; multi -> a check, or a "xN" unit count
     * chip once the same gift is picked more than once (review F12). */
    var dotInner = isSingle
      ? '<span class="ibg-card-dot-inner"></span>'
      : '<span class="ibg-card-check">' + SVG.check + '</span>' +
        '<span class="ibg-card-count">' + (c.count >= 2 ? '×' + c.count : '') + '</span>';
    var dot = '<span class="ibg-card-dot" aria-hidden="true">' + dotInner + '</span>';

    var brand = c.brand ? '<div class="ibg-card-brand">' + esc(c.brand) + '</div>' : '';

    var foot = '';
    if (c.size || c.hasValue) {
      var sizeEl = c.size ? '<span class="ibg-card-size">' + esc(c.size) + '</span>' : '<span></span>';
      var valueEl = c.hasValue
        ? '<span class="ibg-card-value"><span class="ibg-card-value-strike">' + esc(c.valueStrike) +
          '</span><span class="ibg-card-value-tag">' + esc(c.valueTag) + '</span></span>'
        : '';
      foot = '<div class="ibg-card-foot">' + sizeEl + valueEl + '</div>';
    }

    var cls = 'ibg-card';
    if (c.selected) cls += ' is-selected';
    if (c.count >= 2) cls += ' is-dup';
    if (c.dim) cls += ' is-dim';
    if (c.oos) cls += ' is-oos';

    return '<button type="button" class="' + cls + '" data-ibg-tile="' + esc(c.variantId) + '"' +
        ' aria-pressed="' + (c.selected ? 'true' : 'false') + '"' + (c.oos ? ' disabled aria-disabled="true"' : '') + '>' +
        '<span class="ibg-card-media">' +
          '<span class="ibg-card-grad" style="background:' + esc(c.gradient) + '"></span>' +
          badge + media +
        '</span>' +
        dot + brand +
        '<span class="ibg-card-name">' + esc(c.name) + '</span>' +
        foot +
        '<span class="ibg-card-veil" aria-hidden="true"></span>' +
        '<span class="ibg-card-ring" aria-hidden="true"></span>' +
      '</button>';
  }

  function renderModalView(view) {
    if (!view) return '';

    var panelCls = 'ibg-modal-panel' + (view.isSingle ? ' is-single' : ' is-multi') +
      (view.isMobile ? ' is-mobile' : '') + (view.added ? ' is-added' : '') +
      (view.adding ? ' is-adding' : '') + (view.errored ? ' is-error' : '') +
      (view.atCap ? ' is-atcap' : '');

    var grab = view.isMobile ? '<div class="ibg-grab" aria-hidden="true"></div>' : '';

    var marker = view.marker.show
      ? '<span class="ibg-offer-marker">' + esc(view.marker.label) + '</span>'
      : '';

    /* Header. */
    var head =
      '<div class="ibg-head">' +
        '<span class="ibg-medallion" aria-hidden="true">' + SVG.gift + '</span>' +
        '<div class="ibg-head-text">' +
          '<div class="ibg-eyebrow-row"><span class="ibg-eyebrow">Your free gift</span>' + marker + '</div>' +
          '<h2 class="ibg-head-title">Choose your <em>free</em> ' + esc(view.giftNoun) + '</h2>' +
          '<p class="ibg-head-sub">You’ve unlocked <strong>' + view.maxPick + '</strong>' +
            (view.reopened ? ' more' : '') + ' free ' + esc(view.giftNoun) +
            ' with this order — pick your ' + esc(view.favNoun) + ' below.</p>' +
        '</div>' +
        '<button type="button" class="ibg-close" data-ibg-close aria-label="Close">' + SVG.close + '</button>' +
      '</div>';

    /* Progress (multi) or single-prompt line. */
    var progress = view.isSingle
      ? '<div class="ibg-single-prompt">' + esc(view.singlePrompt) + '</div>'
      : '<div class="ibg-progress">' +
          '<div class="ibg-progress-track"><div class="ibg-progress-fill" style="width:' + view.pct + '%"></div></div>' +
          '<span class="ibg-progress-label">' + view.count + ' of ' + view.maxPick + ' chosen</span>' +
        '</div>';

    /* Availability row. */
    var avail =
      '<div class="ibg-avail">' +
        '<span class="ibg-avail-count">' + view.total + ' gift' + (view.total === 1 ? '' : 's') + ' available</span>' +
        '<span class="ibg-avail-status ibg-avail-status--' + view.availTone + '">' + esc(view.availStatus) + '</span>' +
      '</div>';

    /* Cards. */
    var cardsHtml = '';
    for (var i = 0; i < view.cards.length; i++) cardsHtml += cardHtml(view.cards[i], view.isSingle);
    var grid = '<div class="ibg-grid" style="grid-template-columns:' + esc(view.grid.template) +
      ';justify-content:' + esc(view.grid.justify) + '">' + cardsHtml + '</div>';

    var scroll =
      '<div class="ibg-scroll-wrap">' +
        '<div class="ibg-fade ibg-fade--top" aria-hidden="true"></div>' +
        '<div class="ibg-fade ibg-fade--bottom" aria-hidden="true"></div>' +
        '<div class="ibg-scroll">' + avail + grid + '</div>' +
      '</div>';

    /* Footer. The summary is a live region so the confirmed "Added ✓" (and any
     * "Couldn't add") is announced to screen readers (review F19); with the
     * async commit flow it only reads success AFTER the mutation resolves. */
    var foot =
      '<div class="ibg-foot">' +
        '<div class="ibg-summary ibg-summary--' + view.summaryTone + '" role="status" aria-live="polite">' +
          '<span class="ibg-summary-top">' + esc(view.summaryTop) + '</span>' +
          '<span class="ibg-summary-sub">' + esc(view.summarySub) + '</span>' +
        '</div>' +
        '<div class="ibg-cta-row">' +
          '<button type="button" class="ibg-cta' + (view.ctaEnabled ? '' : ' is-disabled') + '" data-ibg-commit' +
            (view.ctaEnabled ? '' : ' aria-disabled="true"') + (view.busy ? ' aria-busy="true"' : '') + '>' +
            '<span class="ibg-cta-spinner" aria-hidden="true"></span>' +
            '<span class="ibg-cta-check">' + SVG.check + '</span>' +
            '<span class="ibg-cta-label">' + esc(view.ctaLabel) + '</span>' +
          '</button>' +
        '</div>' +
      '</div>';

    var headerZone = '<div class="ibg-header-zone">' + head + progress + '</div>';

    return '' +
      '<div class="ibg-modal-backdrop" data-ibg-close></div>' +
      '<div class="ibg-modal" role="dialog" aria-modal="true" aria-label="Choose your free gift" tabindex="-1">' +
        '<div class="' + panelCls + '">' +
          '<div class="ibg-topbar" aria-hidden="true"></div>' +
          grab + headerZone + scroll + foot +
        '</div>' +
      '</div>';
  }

  /* Controller convenience: build the view then render it in one call. Kept so
   * the pure path (buildModalView + renderModalView) stays independently
   * testable while the live code has a single entry point. */
  function renderModalHTML(input) {
    return renderModalView(buildModalView(input));
  }

  /* ------------------------------------------------------------------ *
   * Drawer re-entry row + open-once logic (pure).
   * ------------------------------------------------------------------ */

  function lostCardHtml(card) {
    return '<div class="ibg-lost" data-ibg-rule="' + esc(card.ruleId) + '">' +
      '<span class="ibg-lost-seal" aria-hidden="true">' + SVG.gift + '</span>' +
      '<span class="ibg-lost-text">' +
        '<span class="ibg-eyebrow">Free gift</span>' +
        '<span class="ibg-lost-title">Free-gift offer removed — the qualifying items left your cart</span>' +
      '</span>' +
    '</div>';
  }

  /* One string per pending rule capturing WHAT is pending. A higher slot count
   * or a newly-OOS chosen option changes the signature, so those re-open a
   * previously dismissed modal; the same pending state never re-opens. */
  function pendingSignature(cards) {
    var sig = {};
    for (var i = 0; i < (cards || []).length; i++) {
      var card = cards[i];
      if (card.kind !== 'choice') continue;
      sig[card.ruleId] = 's' + card.slots +
        (card.chosenOos && card.chosenOos.length ? ':oos:' + card.chosenOos.join(',') : '');
    }
    return sig;
  }

  /* seen: ruleId -> last signature auto-shown. Returns whether the modal should
   * auto-open and the updated seen map (pure — storage is the controller's). */
  function computeAutoOpen(cards, seen) {
    seen = seen || {};
    var sig = pendingSignature(cards);
    var shouldOpen = false;
    var next = {};
    /* Only rules with a live pending choice keep their seen entry. A rule whose
     * offer was resolved or dequalified is forgotten, so a FRESH episode
     * (re-qualifying later) auto-opens again instead of staying suppressed. */
    for (var ruleId in seen) {
      if (!Object.prototype.hasOwnProperty.call(seen, ruleId)) continue;
      if (sig[ruleId] !== undefined) next[ruleId] = seen[ruleId];
    }
    for (var id in sig) {
      if (!Object.prototype.hasOwnProperty.call(sig, id)) continue;
      if (next[id] !== sig[id]) {
        shouldOpen = true;
        next[id] = sig[id];
      }
    }
    return { shouldOpen: shouldOpen, seen: next };
  }

  function pendingCount(cards) {
    return choiceCardsOf(cards).length;
  }

  /* Slim drawer row — the re-entry affordance while choices are pending, plus
   * any quiet "offer removed" notes. */
  function renderEntryRowHTML(cards) {
    if (!cards || !cards.length) return '';
    var html = '';
    var pending = pendingCount(cards);
    if (pending > 0) {
      var label = pending > 1
        ? 'Choose your ' + pending + ' free gifts'
        : 'Choose your free gift';
      html += '<button type="button" class="ibg-row" data-ibg-open>' +
        '<span class="ibg-row-seal">' + SVG.gift + '</span>' +
        '<span class="ibg-row-text"><span class="ibg-eyebrow">Your free gift</span>' +
        '<span class="ibg-row-title">' + esc(label) + '</span></span>' +
        '<span class="ibg-row-arrow" aria-hidden="true">&rsaquo;</span>' +
        '</button>';
    }
    for (var i = 0; i < cards.length; i++) {
      if (cards[i].kind === 'lost') html += lostCardHtml(cards[i]);
    }
    return html;
  }

  /* ------------------------------------------------------------------ *
   * Engine writes — the ONLY mutations this file performs.
   * ------------------------------------------------------------------ */

  function commitSelection(engine, ruleId, variantIds) {
    if (!engine || typeof engine.selectChoice !== 'function') return;
    /* Append-only (Noah 2026-07-21): committed picks already live in the engine
     * and the cart, and the engine now allows duplicate variants across slots —
     * so a clear-then-reselect (or re-sending a committed pick) would drop or
     * double-insert gifts on the next slot bump. Send exactly the new picks the
     * shopper made in this modal session. */
    var picks = [];
    for (var i = 0; i < variantIds.length; i++) {
      if (variantIds[i]) picks.push(variantIds[i]);
    }
    if (!picks.length) return;
    /* Batch commit so a multi-pick yields ONE mutation-result carrying the full
     * ack set (a per-pick selectChoice loop reconciles separately and would
     * split the acks across events, stalling the commit machine's all-added
     * check). Fall back to the loop for older engine builds. */
    if (typeof engine.selectChoices === 'function') {
      engine.selectChoices(ruleId, picks);
    } else {
      for (var j = 0; j < picks.length; j++) engine.selectChoice(ruleId, picks[j]);
    }
  }

  /* ------------------------------------------------------------------ *
   * Live controller (DOM). Everything below only runs in a browser.
   * ------------------------------------------------------------------ */

  var SEEN_STORAGE_KEY = 'ibg:choice-seen';
  var MODAL_ROOT_ID = 'ib-gift-choice-modal-root';
  var ADDED_BEAT_MS = 1200;  /* in-modal confirmation beat before advancing */
  var COMMIT_TIMEOUT_MS = 6000; /* a missing mutation-result can never wedge the modal (F2) */

  var ctrl = {
    modalRoot: null,
    modalBound: false,
    modalOpen: false,
    lastFocus: null,
    prevRuleIds: {},        /* choice ruleIds shown last render */
    lastCards: [],
    lastTargets: [],
    lastCart: null,         /* last AUTHORITATIVE cart snapshot (for an idempotent retry, R2-4) */
    lastRewardKeys: {},     /* reward cart-line keys from the last cart:updated (F14) */
    shellSig: null,         /* identity of the currently-mounted modal shell */
    selByRule: {},          /* ruleId -> working selection multiset (array, dups ok) */
    sessionRules: [],       /* ordered ruleIds this modal session — drives the marker */
    scrollLock: null,       /* preserved scroll position while the modal locks the body */
    /* Async commit state machine (F2): { phase, ruleId, variantIds, card, timer, beatTimer } */
    commit: { phase: 'idle', ruleId: null, variantIds: [], card: null, timer: null, beatTimer: null },
    suppressToastVariants: {} /* variantIds committed via the modal — no toast */
  };

  function seenGet() {
    try {
      var raw = sessionStorage.getItem(SEEN_STORAGE_KEY);
      var parsed = raw ? JSON.parse(raw) : {};
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (err) { return {}; }
  }

  function seenSet(map) {
    try { sessionStorage.setItem(SEEN_STORAGE_KEY, JSON.stringify(map)); } catch (err) { /* noop */ }
  }

  function viewportW() {
    return (typeof window !== 'undefined' && window.innerWidth) || 1200;
  }

  /* Is this element in a currently-rendered, visible subtree? Walks ancestors for
   * display:none / visibility:hidden so the hidden header drawer on /cart is
   * never chosen (review F13). Robust to whatever the drawer uses to hide. */
  function isVisible(el) {
    if (!el || typeof window === 'undefined' || !window.getComputedStyle) return !!el;
    if (el.offsetParent === null) {
      var pos = window.getComputedStyle(el).position;
      if (pos !== 'fixed' && pos !== 'sticky') return false; /* an ancestor is display:none */
    }
    var node = el;
    while (node && node.nodeType === 1) {
      var cs = window.getComputedStyle(node);
      if (cs.display === 'none' || cs.visibility === 'hidden') return false;
      node = node.parentElement;
    }
    return true;
  }

  function onCartPage() {
    try {
      if (typeof location !== 'undefined' && /(^|\/)cart(\/|$|\?|#)/.test(location.pathname)) return true;
    } catch (e) { /* noop */ }
    var body = hasDoc() && document.body;
    return !!(body && body.className && /(^|\s)template-cart(\s|$)/.test(body.className));
  }

  /* Pick the mount for the surface the shopper is actually looking at: the cart
   * page's own anchor on /cart, the drawer's otherwise — then fall back to any
   * VISIBLE anchor, then a visible legacy container. Never mounts into a hidden
   * subtree (the /cart-drawer-invisible-row bug, review F13). */
  function resolveMount() {
    if (!hasDoc() || !document.querySelectorAll) return null;
    var preferSurface = onCartPage() ? 'page' : 'drawer';

    var mounts = document.querySelectorAll('[data-ib-gift-mount]');
    var visibleMount = null, preferredMount = null;
    for (var i = 0; i < mounts.length; i++) {
      if (!isVisible(mounts[i])) continue;
      if (!visibleMount) visibleMount = mounts[i];
      if (mounts[i].getAttribute && mounts[i].getAttribute('data-ib-gift-surface') === preferSurface) {
        preferredMount = mounts[i];
      }
    }
    if (preferredMount) return preferredMount;
    if (visibleMount) return visibleMount;

    /* Legacy anchors (themes without the explicit mounts), visible-first. */
    var scrollables = document.querySelectorAll('.cart__scrollable');
    for (var s = 0; s < scrollables.length; s++) {
      if (isVisible(scrollables[s])) return scrollables[s];
    }
    var items = document.querySelectorAll('.cart__items');
    for (var k = 0; k < items.length; k++) {
      if (isVisible(items[k]) && items[k].parentNode) return items[k].parentNode;
    }
    return null; /* nothing visible — a hidden row is worse than none */
  }

  /* Mount targets for the re-entry rail. With explicit anchors we render into
   * EVERY [data-ib-gift-mount] (both the drawer and the cart page), so the row
   * survives a surface switch — the drawer being hidden at dismissal no longer
   * strands the row in the wrong surface (review R2-10). Legacy themes fall back
   * to the single visible mount. */
  function rowMountTargets() {
    if (!hasDoc() || !document.querySelectorAll) return [];
    var mounts = document.querySelectorAll('[data-ib-gift-mount]');
    if (mounts.length) return [].slice.call(mounts);
    var legacy = resolveMount();
    return legacy ? [legacy] : [];
  }

  function ownRowChild(mount) {
    var kids = mount.children || [];
    for (var i = 0; i < kids.length; i++) {
      if (kids[i].className && (' ' + kids[i].className + ' ').indexOf(' ibg-root ') !== -1) return kids[i];
    }
    return null;
  }

  /* Ensure an .ibg-root rail child exists in each mount target; return them all. */
  function ensureRowRoots() {
    var targets = rowMountTargets();
    var roots = [];
    for (var i = 0; i < targets.length; i++) {
      var mount = targets[i];
      var root = ownRowChild(mount);
      if (!root) {
        root = document.createElement('div');
        root.className = 'ibg-root';
        root.setAttribute('role', 'region');
        root.setAttribute('aria-label', 'Your free gifts');
        if (mount.firstChild) mount.insertBefore(root, mount.firstChild);
        else mount.appendChild(root);
      }
      bindRowRoot(root);
      roots.push(root);
    }
    return roots;
  }

  function ensureModalRoot() {
    if (ctrl.modalRoot && ctrl.modalRoot.isConnected) return ctrl.modalRoot;
    if (!hasDoc() || !document.body || !document.createElement) return null;
    var root = ctrl.modalRoot || document.getElementById(MODAL_ROOT_ID);
    if (!root) {
      root = document.createElement('div');
      root.id = MODAL_ROOT_ID;
      root.className = 'ibg-modal-root';
      root.hidden = true;
    }
    if (root.parentNode !== document.body) document.body.appendChild(root);
    ctrl.modalRoot = root;
    bindModalRoot(root);
    return root;
  }

  function bindRowRoot(root) {
    if (!root.addEventListener || root.__ibgBound) return;
    root.__ibgBound = true; /* per-element — each surface's rail binds its own opener */
    root.addEventListener('click', function (event) {
      var opener = closestAttrIn(root, event.target, 'data-ibg-open');
      if (opener) openModal();
    });
  }

  function bindModalRoot(root) {
    if (ctrl.modalBound || !root.addEventListener) return;
    ctrl.modalBound = true;

    root.addEventListener('click', function (event) {
      var closer = closestAttrIn(root, event.target, 'data-ibg-close');
      if (closer) { closeModal(true); return; }

      var commit = closestAttrIn(root, event.target, 'data-ibg-commit');
      if (commit) { onCommit(); return; }

      var tile = closestAttrIn(root, event.target, 'data-ibg-tile');
      if (tile) onTile(tile.getAttribute('data-ibg-tile'));
    });

    root.addEventListener('keydown', function (event) {
      if (!ctrl.modalOpen) return;
      if (event.key === 'Escape' || event.key === 'Esc') {
        event.preventDefault();
        closeModal(true);
        return;
      }
      if (event.key !== 'Tab') return;

      /* Containment-based focus trap (review F17): keep focus inside the panel
       * on EVERY Tab, not only when it sits on the first/last descendant. The
       * dialog container holds initial focus via tabindex=-1, so a Shift+Tab
       * from it used to escape; here any Tab that would leave the focusables
       * wraps to the opposite end. */
      var focusables = root.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])');
      if (!focusables.length) { event.preventDefault(); return; }
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      var active = document.activeElement;
      var within = false;
      for (var i = 0; i < focusables.length; i++) { if (focusables[i] === active) { within = true; break; } }

      if (!within) {
        /* focus is on the dialog container (or otherwise outside the tab ring) */
        event.preventDefault();
        var target = event.shiftKey ? last : first;
        if (target.focus) target.focus();
        return;
      }
      if (event.shiftKey && active === first) {
        event.preventDefault();
        if (last.focus) last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        if (first.focus) first.focus();
      }
    });
  }

  function closestAttrIn(root, el, attr) {
    while (el && el !== root) {
      if (el.getAttribute && el.getAttribute(attr) != null) return el;
      el = el.parentNode;
    }
    return null;
  }

  function commitActive() { return ctrl.commit.phase !== 'idle'; }

  /* The rule on stage: the one whose commit is resolving (held so the sync
   * ibgift:state that selectChoice dispatches can't swap it out mid-flight),
   * else the first still-pending rule. A fully-committed rule leaves the
   * engine's pending set on its own; a PARTIALLY-committed rule stays pending
   * with a reduced remaining count and is re-staged after the beat (F11). */
  function currentCard() {
    if (commitActive() && ctrl.commit.card) return ctrl.commit.card;
    var pending = choiceCardsOf(ctrl.lastCards);
    return pending.length ? pending[0] : null;
  }

  function currentSelection(card) {
    if (commitActive()) return ctrl.commit.variantIds;
    return ctrl.selByRule[card.ruleId] || [];
  }

  /* Register every live rule in session order so the "Offer N of M" marker has a
   * stable position that a partial commit (same rule) doesn't disturb (F11). */
  function ensureSessionRules() {
    var pending = choiceCardsOf(ctrl.lastCards);
    for (var i = 0; i < pending.length; i++) {
      if (ctrl.sessionRules.indexOf(pending[i].ruleId) === -1) ctrl.sessionRules.push(pending[i].ruleId);
    }
    if (commitActive() && ctrl.commit.ruleId && ctrl.sessionRules.indexOf(ctrl.commit.ruleId) === -1) {
      ctrl.sessionRules.push(ctrl.commit.ruleId);
    }
  }

  function buildView() {
    var card = currentCard();
    if (!card) return null;
    ensureSessionRules();
    var pos = ctrl.sessionRules.indexOf(card.ruleId);
    return buildModalView({
      card: card,
      selectedIds: currentSelection(card),
      markerPosition: (pos === -1 ? 0 : pos) + 1,
      markerTotal: Math.max(ctrl.sessionRules.length, 1),
      commitPhase: ctrl.commit.phase,
      isMobile: viewportW() < 720,
      viewportW: viewportW(),
      showValue: readConfig().showValue
    });
  }

  function openModal() {
    var root = ensureModalRoot();
    if (!root) return false;
    if (!ctrl.modalOpen) {
      ctrl.modalOpen = true;
      ctrl.lastFocus = (hasDoc() && document.activeElement) || null;
      /* Fresh session. */
      ctrl.selByRule = {};
      ctrl.sessionRules = [];
      resetCommit();
      lockBodyScroll();
    }
    render();
    focusPanel();
    return true;
  }

  function focusPanel() {
    if (!ctrl.modalRoot || !ctrl.modalRoot.querySelector) return;
    var dialog = ctrl.modalRoot.querySelector('.ibg-modal');
    if (dialog && dialog.focus) { try { dialog.focus(); } catch (err) { /* noop */ } }
  }

  function closeModal(restoreFocus) {
    if (!ctrl.modalOpen) return;
    ctrl.modalOpen = false;
    resetCommit();
    ctrl.selByRule = {};
    ctrl.sessionRules = [];
    unlockBodyScroll();
    render();
    if (restoreFocus && ctrl.lastFocus && ctrl.lastFocus.focus) {
      try { ctrl.lastFocus.focus(); } catch (err) { /* noop */ }
    }
    ctrl.lastFocus = null;
  }

  function resetCommit() {
    if (ctrl.commit.timer) { clearTimeout(ctrl.commit.timer); }
    if (ctrl.commit.beatTimer) { clearTimeout(ctrl.commit.beatTimer); }
    ctrl.commit = { phase: 'idle', ruleId: null, variantIds: [], card: null, timer: null, beatTimer: null };
  }

  /* Background scroll-lock with position preserve/restore (review F18). */
  function lockBodyScroll() {
    if (!hasDoc() || !document.body || ctrl.scrollLock) return;
    var y = (typeof window !== 'undefined' && (window.scrollY || window.pageYOffset)) || 0;
    var body = document.body;
    ctrl.scrollLock = {
      y: y,
      prev: { position: body.style.position, top: body.style.top, width: body.style.width, overflow: body.style.overflow }
    };
    body.style.position = 'fixed';
    body.style.top = -y + 'px';
    body.style.width = '100%';
    body.style.overflow = 'hidden';
  }

  function unlockBodyScroll() {
    if (!hasDoc() || !document.body || !ctrl.scrollLock) return;
    var lock = ctrl.scrollLock;
    ctrl.scrollLock = null;
    var body = document.body;
    body.style.position = lock.prev.position || '';
    body.style.top = lock.prev.top || '';
    body.style.width = lock.prev.width || '';
    body.style.overflow = lock.prev.overflow || '';
    if (typeof window !== 'undefined' && window.scrollTo) {
      try { window.scrollTo(0, lock.y); } catch (e) { /* noop */ }
    }
  }

  /* Toggle a tile's selection UNIT. Caps with the REMAINING slots (view.maxPick)
   * and stores exactly the sanitized selection the view shows — one authority,
   * so the commit can never send a hidden pick (review F10). */
  function onTile(variantId) {
    if (commitActive()) return; /* selection is frozen while a commit resolves */
    var view = buildView();
    if (!view) return;
    var card = currentCard();
    var opt = optionOf(card, String(variantId));
    if (!opt || opt.oos) return; /* OOS tiles never enter the selection */
    ctrl.selByRule[card.ruleId] = toggleSelection(view.selectedIds, String(variantId), view.maxPick);
    render();
  }

  function optionOf(card, variantId) {
    for (var i = 0; i < (card.options || []).length; i++) {
      if (card.options[i].variantId === variantId) return card.options[i];
    }
    return null;
  }

  function rulePending(ruleId) {
    var pending = choiceCardsOf(ctrl.lastCards);
    for (var i = 0; i < pending.length; i++) {
      if (pending[i].ruleId === String(ruleId)) return true;
    }
    return false;
  }

  /* Has this rule vanished from the engine state entirely (dequalified mid-flight)
   * — no choice entry and no reward target? A satisfied/kept rule still appears
   * in choices, so this is true only for a genuine drop, not transient limbo. */
  function ruleGoneFromDetail(detail, ruleId) {
    ruleId = String(ruleId);
    var choices = (detail && detail.choices) || [];
    for (var i = 0; i < choices.length; i++) {
      if (choices[i] && String(choices[i].ruleId) === ruleId) return false;
    }
    var targets = (detail && detail.targets) || [];
    for (var t = 0; t < targets.length; t++) {
      if (targets[t] && String(targets[t].ruleId) === ruleId) return false;
    }
    return true;
  }

  /* CTA. In the error state the button is Retry; otherwise it commits the
   * sanitized selection and enters the async 'adding' state (review F2). */
  function onCommit() {
    if (ctrl.commit.phase === 'error') { retryCommit(); return; }
    if (commitActive()) return; /* adding / added — ignore */
    var view = buildView();
    if (!view || !view.ctaEnabled) return;
    var card = currentCard();
    var chosen = view.selectedIds.slice(); /* AUTHORITATIVE — exactly what is shown */
    if (!chosen.length) return;
    startCommit(card, card.ruleId, chosen);
  }

  function startCommit(card, ruleId, variantIds) {
    ctrl.commit = commitTransition(ctrl.commit, { type: 'commit', ruleId: ruleId, variantIds: variantIds });
    ctrl.commit.card = card;
    /* Suppress the reward-line toast — the in-modal beat is the confirmation.
     * The silent auto-add path keeps its toast (this set stays empty for it). */
    for (var i = 0; i < variantIds.length; i++) ctrl.suppressToastVariants[String(variantIds[i])] = true;
    ctrl.commit.timer = setTimeout(onCommitTimeout, COMMIT_TIMEOUT_MS);
    render(); /* "Adding…", CTA locked */
    /* selectChoice may dispatch ibgift:state synchronously; the commit phase
     * keeps the card on stage and blocks the auto-close through that nested pass. */
    commitSelection(engineApi(), ruleId, variantIds);
  }

  function retryCommit() {
    if (ctrl.commit.phase !== 'error') return;
    var ruleId = ctrl.commit.ruleId, variantIds = ctrl.commit.variantIds;
    ctrl.commit = commitTransition(ctrl.commit, { type: 'retry' });
    ctrl.commit.card = currentCard();
    for (var i = 0; i < variantIds.length; i++) ctrl.suppressToastVariants[String(variantIds[i])] = true;
    ctrl.commit.timer = setTimeout(onCommitTimeout, COMMIT_TIMEOUT_MS);
    render();
    /* Idempotent retry (review R2-4): these picks are already in engine storage
     * from the first attempt (kept across a transient failure), so re-issuing
     * selectChoice would double-append. Re-trigger a reconcile of the stored
     * picks instead — no append. Fall back to a re-send only if the engine can't
     * reconcile (older engine or no cached cart), where the engine's own prune
     * still converges duplicates. */
    var engine = engineApi();
    if (engine && typeof engine.reconcile === 'function' && ctrl.lastCart) {
      try { engine.reconcile(ctrl.lastCart); }
      catch (e) { commitSelection(engine, ruleId, variantIds); }
    } else {
      commitSelection(engine, ruleId, variantIds);
    }
  }

  function onCommitTimeout() {
    if (ctrl.commit.phase !== 'adding') return;
    var card = ctrl.commit.card;
    ctrl.commit = commitTransition(ctrl.commit, { type: 'timeout' });
    ctrl.commit.card = card;
    onCommitResolved();
  }

  /* The engine's mutation-result is the ONLY truthful confirmation that a gift
   * landed in the cart (review F2; team-lead 2026-07-21 — ibgift:state carries
   * DESIRED state, not cart state, so it can never confirm). Handled for both
   * 'adding' and 'error' (a transient failure the engine self-retried, or a
   * survivor auto-add, whose ok arrives after the failure). The reducer matches
   * OUR committed variant + failedRules; a survivor lands a DIFFERENT variant of
   * the same rule, so we also accept an ok carrying any option of the staged
   * card. */
  function onMutationResult(event) {
    if (ctrl.commit.phase !== 'adding' && ctrl.commit.phase !== 'error') return;
    var detail = (event && event.detail) || {};
    var card = ctrl.commit.card;
    var next = commitTransition(ctrl.commit, {
      type: 'result',
      ok: detail.ok,
      addedVariantIds: detail.addedVariantIds,
      failedRules: detail.failedRules
    });
    /* Survivor / mixed full-satisfaction: the reducer matches OUR committed
     * variants, but a single-survivor auto-add lands a DIFFERENT variant of the
     * same rule. Confirm only when the rule's added units FULLY cover the commit
     * (>= committed slot count) — never on a single hit of a multi-pick commit
     * (review R2-3). */
    if (next.phase === ctrl.commit.phase && detail.ok && card &&
        addedCountForRule(card, detail.addedVariantIds) >= ctrl.commit.variantIds.length &&
        ctrl.commit.variantIds.length > 0) {
      next = { phase: 'added', ruleId: ctrl.commit.ruleId, variantIds: ctrl.commit.variantIds };
    }
    if (next.phase === ctrl.commit.phase) return; /* a partial / unrelated result — keep waiting */
    if (ctrl.commit.timer) { clearTimeout(ctrl.commit.timer); ctrl.commit.timer = null; }
    next.card = card;
    ctrl.commit = next;
    onCommitResolved();
  }

  /* How many of the acknowledged-added variants belong to the staged rule — a
   * committed pick that landed, or a single-survivor the engine auto-added in its
   * place (team-lead 2026-07-21). Used to gate confirmation on FULL satisfaction. */
  function addedCountForRule(card, addedVariantIds) {
    var n = 0, av = addedVariantIds || [];
    for (var i = 0; i < av.length; i++) {
      if (optionOf(card, String(av[i]))) n++;
    }
    return n;
  }

  function onCommitResolved() {
    if (ctrl.commit.phase === 'added') {
      /* Confirmed: clear the working selection, play the beat, then advance. */
      delete ctrl.selByRule[ctrl.commit.ruleId];
      render();
      ctrl.commit.beatTimer = setTimeout(advanceAfterBeat, ADDED_BEAT_MS);
    } else if (ctrl.commit.phase === 'error') {
      /* Failed / timed out: no reward line will land, so release the toast
       * suppression, keep the rule staged, and offer Retry. Do NOT close, do
       * NOT mark the pending as seen. */
      for (var i = 0; i < ctrl.commit.variantIds.length; i++) {
        delete ctrl.suppressToastVariants[String(ctrl.commit.variantIds[i])];
      }
      render();
    }
  }

  function advanceAfterBeat() {
    resetCommit();
    if (!ctrl.modalOpen) { render(); return; } /* closed out from under the beat */
    /* currentCard() now reads the authoritative post-commit state: the next
     * queued rule, or the SAME rule with a reduced remaining count after a
     * partial commit (F11), or nothing left — in which case close. */
    if (currentCard()) {
      render();
      focusPanel();
    } else {
      closeModal(true);
    }
  }

  /* ------------------------------------------------------------------ *
   * Render pipeline
   * ------------------------------------------------------------------ */

  function onState(event) {
    try {
      var detail = (event && event.detail) || {};
      var model = buildModel(detail, ctrl.prevRuleIds);
      /* The engine's state is authoritative for the pending set. A commit in
       * flight holds its own card (ctrl.commit.card) through this pass, so a
       * fully-resolved rule simply leaves here and a partially-committed rule
       * stays with a reduced remaining — no separate optimistic-drop filter. */
      ctrl.lastCards = model.cards;
      ctrl.prevRuleIds = model.activeRuleIds;
      ctrl.lastTargets = detail.targets || [];

      /* Error watch (team-lead 2026-07-21). ibgift:state carries DESIRED state,
       * not cart state, so it can NEVER confirm a gift — the only state-driven
       * transitions out of 'error' are ones that don't claim success:
       *   (a) our rule re-presents as a PENDING choice card → leave the error
       *       into the refreshed picker (the dead option arrives greyed via oos).
       *   (b) our rule vanishes from the state entirely (dequalified mid-flight)
       *       → drop the commit so the normal pending-gone close / advance to the
       *       next queued rule applies (it was blocked by the 'error' phase).
       * Actual success is confirmed ONLY by a mutation-result ok (onMutationResult);
       * anything else keeps the Retry CTA. */
      if (ctrl.commit.phase === 'error') {
        if (rulePending(ctrl.commit.ruleId) || ruleGoneFromDetail(detail, ctrl.commit.ruleId)) {
          resetCommit();
        }
      }

      /* HC - modal auto-open-once (decisions §8 addendum 2) - 2026-07-20
       * A NEW pending signature (new rule, higher slot count, or a chosen option
       * going OOS) opens the modal once; the same pending state never re-opens
       * after dismissal. The seen-set persists for the session. */
      var auto = computeAutoOpen(ctrl.lastCards, seenGet());
      if (auto.shouldOpen && !ctrl.modalOpen) {
        if (openModal()) seenSet(auto.seen);
      } else {
        /* Persist even when nothing opens: computeAutoOpen PRUNES entries for
         * rules with no live pending, and that forgetting must reach storage —
         * otherwise a stale "seen" suppresses the re-offer when the shopper
         * re-earns the same slot count later (Noah live repro 2026-07-21). */
        seenSet(auto.seen);
      }
      /* Never yank the modal out from under a commit that is still resolving. */
      if (ctrl.modalOpen && !commitActive() && pendingCount(ctrl.lastCards) === 0) closeModal(false);

      render();
      decorateLineItems();
    } catch (err) { /* fail open */ }
  }

  function render() {
    /* Re-entry rail (+ quiet lost notes) into every surface's mount (R2-10). */
    var rowRoots = ensureRowRoots();
    var rowHtml = renderEntryRowHTML(ctrl.lastCards);
    for (var r = 0; r < rowRoots.length; r++) {
      var rr = rowRoots[r];
      if (rr.__ibgHtml !== rowHtml) { rr.innerHTML = rowHtml; rr.__ibgHtml = rowHtml; }
      rr.hidden = !rowHtml;
    }

    /* Overlay modal. */
    var modalRoot = ensureModalRoot();
    if (!modalRoot) return;

    if (!ctrl.modalOpen) {
      if (ctrl.shellSig !== null) { modalRoot.innerHTML = ''; ctrl.shellSig = null; }
      modalRoot.hidden = true;
      unlockBodyScroll(); /* centralize release — every hide path frees the lock (R2-4) */
      return;
    }

    var view = buildView();
    if (!view) {
      /* Nothing left to show (queue drained mid-render): hide WITHOUT routing
       * through closeModal, so release the scroll lock here or the body strands
       * at position:fixed/overflow:hidden (review R2-4). */
      ctrl.modalOpen = false;
      resetCommit();
      modalRoot.innerHTML = '';
      ctrl.shellSig = null;
      modalRoot.hidden = true;
      unlockBodyScroll();
      return;
    }

    /* Rebuild the shell only when the rule, options, or commit phase changes; a
     * mere selection change updates in place so transitions survive. */
    var sig = shellSignature(view);
    if (sig !== ctrl.shellSig) {
      /* Replacing innerHTML destroys the focused node, so focus falls to <body>
       * — and a body-focused keydown never reaches the panel-rooted focus trap,
       * stranding keyboard users outside the dialog (review R2-11). Restore focus
       * into the dialog whenever it was inside before the swap. */
      var hadFocus = !!(hasDoc() && document.activeElement && modalRoot.contains &&
        modalRoot.contains(document.activeElement));
      modalRoot.innerHTML = renderModalView(view);
      ctrl.shellSig = sig;
      if (hadFocus) focusPanel();
    } else {
      applyState(view);
    }
    modalRoot.hidden = false;
  }

  function shellSignature(view) {
    var ids = [];
    for (var i = 0; i < view.cards.length; i++) {
      ids.push(view.cards[i].variantId + (view.cards[i].oos ? 'x' : ''));
    }
    /* Rebuild the shell when the rule, its options, or the commit PHASE changes
     * (idle/adding/added/error each restyle the footer + CTA); a mere selection
     * change stays in-place so the ring glow and progress bar animate. */
    return view.ruleId + '|' + view.maxPick + '|' + view.isMobile + '|' + view.phase + '|' + ids.join(',');
  }

  /* In-place update after a tile toggle — no innerHTML swap. */
  function applyState(view) {
    var root = ctrl.modalRoot;
    if (!root || !root.querySelector) return;

    var panel = root.querySelector('.ibg-modal-panel');
    if (panel) toggleClass(panel, 'is-atcap', view.atCap);

    var fill = root.querySelector('.ibg-progress-fill');
    if (fill) fill.style.width = view.pct + '%';
    setText(root.querySelector('.ibg-progress-label'), view.count + ' of ' + view.maxPick + ' chosen');
    setText(root.querySelector('.ibg-single-prompt'), view.singlePrompt);

    var status = root.querySelector('.ibg-avail-status');
    if (status) {
      status.className = 'ibg-avail-status ibg-avail-status--' + view.availTone;
      status.textContent = view.availStatus;
    }

    setText(root.querySelector('.ibg-summary-top'), view.summaryTop);
    setText(root.querySelector('.ibg-summary-sub'), view.summarySub);

    var cta = root.querySelector('.ibg-cta');
    if (cta) {
      toggleClass(cta, 'is-disabled', !view.ctaEnabled);
      if (view.ctaEnabled) cta.removeAttribute('aria-disabled');
      else cta.setAttribute('aria-disabled', 'true');
    }

    var byVariant = {};
    for (var i = 0; i < view.cards.length; i++) byVariant[view.cards[i].variantId] = view.cards[i];
    var tiles = root.querySelectorAll('.ibg-card[data-ibg-tile]');
    for (var t = 0; t < tiles.length; t++) {
      var el = tiles[t];
      var c = byVariant[el.getAttribute('data-ibg-tile')];
      if (!c) continue;
      toggleClass(el, 'is-selected', c.selected);
      toggleClass(el, 'is-dup', c.count >= 2);
      toggleClass(el, 'is-dim', c.dim);
      var countEl = el.querySelector('.ibg-card-count');
      if (countEl) countEl.textContent = c.count >= 2 ? '×' + c.count : '';
      if (el.setAttribute) el.setAttribute('aria-pressed', c.selected ? 'true' : 'false');
    }
  }

  function toggleClass(el, cls, on) {
    if (!el) return;
    if (el.classList && el.classList.toggle) { el.classList.toggle(cls, !!on); return; }
    var has = (' ' + el.className + ' ').indexOf(' ' + cls + ' ') !== -1;
    if (on && !has) el.className += ' ' + cls;
    else if (!on && has) el.className = (' ' + el.className + ' ').replace(' ' + cls + ' ', ' ').replace(/^\s+|\s+$/g, '');
  }

  function setText(el, text) { if (el) el.textContent = text; }

  /* The set of reward cart-line KEYS from a cart:updated payload — a line is a
   * reward iff it carries the engine's `_ib_reward` property. Keyed by the
   * Shopify line key so identity is authoritative (review F14): a paid line that
   * happens to share a variant with a reward has a DIFFERENT key + no property,
   * and is never badged. */
  function rewardKeySet(cart) {
    var set = {};
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      var line = items[i];
      if (line && line.properties && line.properties._ib_reward && line.key != null) {
        set[String(line.key)] = true;
      }
    }
    return set;
  }

  /* Badge reward line items by their authoritative cart-line key (review F14).
   * Runs on every cart:updated (fresh keys) and on re-renders (cached keys),
   * since the drawer/cart DOM is re-scraped on each mutation. */
  function decorateLineItems() {
    if (!hasDoc() || !document.querySelectorAll) return;
    var keys = ctrl.lastRewardKeys || {};

    var items = document.querySelectorAll('.cart__item');
    for (var j = 0; j < items.length; j++) {
      var item = items[j];
      var key = item.getAttribute && item.getAttribute('data-key');
      var isReward = key != null && keys[String(key)] === true;
      var alreadyFlagged = item.className.indexOf('ibg-is-reward') !== -1;

      if (isReward && !alreadyFlagged) {
        item.className += ' ibg-is-reward';
        var titleWrap = item.querySelector('.cart__item-title');
        if (titleWrap && titleWrap.insertAdjacentHTML) {
          titleWrap.insertAdjacentHTML('afterbegin',
            '<span class="ibg-line-flag">' + SVG.check + 'Free gift</span>');
        }
      } else if (!isReward && alreadyFlagged) {
        item.className = item.className.replace(/\s*ibg-is-reward/g, '');
        var flag = item.querySelector('.ibg-line-flag');
        if (flag && flag.parentNode) flag.parentNode.removeChild(flag);
      }
    }
  }

  /* ------------------------------------------------------------------ *
   * HC - Free-gift toast, theme pattern (Noah, 2026-07-20) - 2026-07-21
   * A brief toast when an engine-inserted reward line NEWLY lands in the cart —
   * deliberately the SAME toast family shoppers already see on collection/promo
   * adds. It fires ONLY for silent auto-adds (single-option rules). A gift the
   * shopper picked in the modal is confirmed by the in-modal "added" beat, so
   * its variant is suppressed here (see ctrl.suppressToastVariants).
   * ------------------------------------------------------------------ */

  var TOAST_VISIBLE_MS = 2500; /* match collection-atc showToastElement */
  var TOAST_EXIT_MS = 300;
  var toastState = { baseline: null, el: null, timer: null };

  function rewardLineKeys(cart) {
    var keys = {};
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      var line = items[i];
      if (!line || !line.properties || !line.properties._ib_reward) continue;
      keys[String(line.key || (line.variant_id + '|' + line.properties._ib_reward))] = line;
    }
    return keys;
  }

  function ensureToastContainer() {
    var container = document.body.querySelector && document.body.querySelector(':scope > .cg-toast-container');
    if (!container) {
      container = document.createElement('div');
      container.className = 'cg-toast-container';
      if (container.setAttribute) container.setAttribute('popover', 'manual');
      document.body.appendChild(container);
    }
    if (container.setAttribute) {
      if (!container.getAttribute || !container.getAttribute('role')) container.setAttribute('role', 'status');
      if (!container.getAttribute || !container.getAttribute('aria-live')) container.setAttribute('aria-live', 'polite');
      if (!container.getAttribute || !container.getAttribute('aria-atomic')) container.setAttribute('aria-atomic', 'true');
    }
    try {
      if (container.matches && !container.matches(':popover-open')) container.showPopover();
    } catch (e) { /* popover unsupported — fixed positioning still applies */ }
    return container;
  }

  function dismissGiftToast() {
    if (toastState.timer) { clearTimeout(toastState.timer); toastState.timer = null; }
    var el = toastState.el;
    toastState.el = null;
    if (!el || !el.parentNode) return;
    var container = el.parentNode;
    if (el.classList && el.classList.remove) el.classList.remove('is-visible');
    else el.className = el.className.replace(/\s*is-visible/g, '');
    setTimeout(function () {
      if (el.parentNode) el.parentNode.removeChild(el);
      if (container && container.className && container.className.indexOf('cg-toast-container') !== -1 &&
          !container.hasChildNodes()) {
        try { container.hidePopover(); } catch (e) {}
        if (container.parentNode) container.parentNode.removeChild(container);
      }
    }, TOAST_EXIT_MS);
  }

  function showGiftToast(meta) {
    if (!hasDoc() || !document.body || !document.createElement) return;
    meta = meta || {};
    dismissGiftToast();

    var container = ensureToastContainer();
    var el = document.createElement('div');
    el.className = 'cg-toast';

    var media = '';
    var image = safeImageUrl(meta.image);
    if (image) {
      media = '<img class="cg-toast__image" src="' + esc(image) + '" alt="">';
    }
    el.innerHTML =
      media +
      '<div class="cg-toast__text">' +
        '<div class="cg-toast__line">Free gift added</div>' +
        '<div class="cg-toast__line cg-toast__line--meta">' + esc(meta.title || '') + '</div>' +
      '</div>';

    container.appendChild(el);
    toastState.el = el;

    var reveal = function () {
      if (toastState.el === el) {
        if (el.classList && el.classList.add) el.classList.add('is-visible');
        else el.className += ' is-visible';
      }
    };
    if (typeof requestAnimationFrame === 'function') requestAnimationFrame(reveal);
    else setTimeout(reveal, 16);

    toastState.timer = setTimeout(dismissGiftToast, TOAST_VISIBLE_MS);
  }

  function trackGiftTransitions(detail) {
    detail = detail || {};
    if (detail.optimistic || detail.countOnly || detail.lightweight) return false;
    var cart = detail.cart || detail.cartState;
    if (!cart || Object.prototype.toString.call(cart.items) !== '[object Array]') return false;
    var current = rewardLineKeys(cart);
    var baseline = toastState.baseline;
    toastState.baseline = current;
    if (baseline === null) return false; /* first observation = page load */
    for (var key in current) {
      if (!Object.prototype.hasOwnProperty.call(current, key)) continue;
      if (baseline[key]) continue;
      var line = current[key] || {};
      /* A gift the shopper chose in the modal is confirmed in-modal — don't
       * double-confirm with a toast. Consume the suppression once. */
      var vid = line.variant_id != null ? String(line.variant_id) : '';
      if (vid && ctrl.suppressToastVariants[vid]) {
        delete ctrl.suppressToastVariants[vid];
        continue;
      }
      showGiftToast({
        title: line.product_title || line.title || '',
        image: line.image || (line.featured_image && line.featured_image.url) || ''
      });
      return true;
    }
    return false;
  }

  /* ------------------------------------------------------------------ *
   * Bootstrap
   * ------------------------------------------------------------------ */

  function init() {
    if (!hasDoc()) return;
    document.addEventListener('ibgift:state', onState);
    /* The engine's reconciler reports each cart mutation's outcome; the async
     * commit flow keys its success/failure beat off this event (review F2). */
    document.addEventListener('ibgift:mutation-result', onMutationResult);
    document.addEventListener('ibgift:choices-updated', function () {
      /* The engine follows a selection with a fresh ibgift:state; this is a
       * cheap re-decorate hook in case a cart swap raced ahead of state. */
      decorateLineItems();
    });
    document.addEventListener('cart:updated', function (event) {
      var detail = (event && event.detail) || {};
      var cart = detail.cart || detail.cartState;
      /* Only an AUTHORITATIVE cart snapshot updates the reward-key set; a
       * count-only / optimistic / cartless event carries no line data and must
       * NOT wipe the badges the last real snapshot established (review R2-12). */
      var authoritative = !detail.optimistic && !detail.countOnly && !detail.lightweight &&
        cart && Object.prototype.toString.call(cart.items) === '[object Array]';
      if (authoritative) {
        ctrl.lastCart = cart;                 /* cached for an idempotent retry (R2-4) */
        ctrl.lastRewardKeys = rewardKeySet(cart);
      }
      decorateLineItems();                    /* re-applies cached keys; safe on any event */
      trackGiftTransitions(detail);
    });
  }

  var api = {
    version: '0.2.0',
    _test: {
      buildModel: buildModel,
      buildCard: buildCard,
      normOption: normOption,
      toggleSelection: toggleSelection,
      commitTransition: commitTransition,
      buildModalView: buildModalView,
      renderModalView: renderModalView,
      renderModalHTML: renderModalHTML,
      renderEntryRowHTML: renderEntryRowHTML,
      pendingSignature: pendingSignature,
      computeAutoOpen: computeAutoOpen,
      pendingCount: pendingCount,
      choiceCardsOf: choiceCardsOf,
      commitSelection: commitSelection,
      trackGiftTransitions: trackGiftTransitions,
      rewardLineKeys: rewardLineKeys,
      rewardKeySet: rewardKeySet,
      showGiftToast: showGiftToast,
      dismissGiftToast: dismissGiftToast,
      toastState: toastState,
      suppressToastVariants: function (vid) { ctrl.suppressToastVariants[String(vid)] = true; },
      esc: esc,
      safeUrl: safeUrl,
      safeImageUrl: safeImageUrl,
      SVG: SVG,
      GRADIENTS: GRADIENTS
    }
  };

  if (typeof window !== 'undefined') {
    window.IBGiftChoiceUI = api;
    if (!window.IB_GIFT_CHOICE_UI_MANUAL) {
      if (hasDoc() && document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
      } else {
        init();
      }
    }
  }
})();
